import { useState, useEffect, useCallback } from "react";
import { API } from "../App";

// Simple hash function for client-side variant assignment
const simpleHash = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash);
};

// Deterministically assign variant based on user ID hash
const hashToVariant = (userId, experimentId, variants) => {
  const hashInput = `${userId}:${experimentId}`;
  const hashValue = simpleHash(hashInput);
  const variantIndex = hashValue % variants.length;
  return variants[variantIndex];
};

// Fetch experiment config for anonymous users
const getExperimentByFeatureArea = async (featureArea) => {
  try {
    const response = await fetch(`${API}/experiments/public/${featureArea}`);
    if (response.ok) {
      return await response.json();
    }
  } catch (error) {
    console.log("Failed to fetch public experiment config");
  }
  return null;
};

/**
 * Custom hook for A/B testing variant assignment and conversion tracking
 * 
 * Usage:
 * ```
 * const { variant, config, trackConversion, loading } = useExperiment("job_recommendations");
 * 
 * // Use variant to render different UI
 * if (variant === "treatment") {
 *   return <NewRecommendationsUI config={config} />;
 * }
 * return <DefaultRecommendationsUI />;
 * 
 * // Track conversions when user completes target action
 * const handleApply = () => {
 *   trackConversion("job_applied", { jobId: "123" });
 * };
 * ```
 */

// Generate or retrieve anonymous session ID for unauthenticated experiment assignment
const getOrCreateAnonymousId = () => {
  const key = 'rekrutor_anon_id';
  let anonId = localStorage.getItem(key);
  if (!anonId) {
    anonId = `anon_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem(key, anonId);
  }
  return anonId;
};

export const useExperiment = (featureArea) => {
  const [variant, setVariant] = useState("control");
  const [config, setConfig] = useState({});
  const [experimentId, setExperimentId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAssignment = async () => {
      try {
        // Include anonymous ID in header for unauthenticated users
        const anonId = getOrCreateAnonymousId();
        
        const response = await fetch(`${API}/experiments/assignment/${featureArea}`, {
          credentials: "include",
          headers: {
            "X-Anonymous-Id": anonId
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setVariant(data.variant);
          setConfig(data.config || {});
          setExperimentId(data.experiment_id);
        } else if (response.status === 401) {
          // For unauthenticated users, use client-side variant assignment based on hash
          const experiment = await getExperimentByFeatureArea(featureArea);
          if (experiment) {
            const assignedVariant = hashToVariant(anonId, experiment.experiment_id, experiment.variants);
            setVariant(assignedVariant.name);
            setConfig(assignedVariant.config || {});
            setExperimentId(experiment.experiment_id);
          }
        }
      } catch (error) {
        console.error("Failed to fetch experiment assignment:", error);
        // Default to control on error
        setVariant("control");
      } finally {
        setLoading(false);
      }
    };

    fetchAssignment();
  }, [featureArea]);

  const trackConversion = useCallback(async (eventType = "conversion", metadata = {}) => {
    if (!experimentId) {
      // Not in any experiment
      return false;
    }

    try {
      const response = await fetch(`${API}/experiments/conversion`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          experiment_id: experimentId,
          event_type: eventType,
          metadata
        }),
      });

      if (response.ok) {
        const data = await response.json();
        return data.tracked;
      }
    } catch (error) {
      console.error("Failed to track conversion:", error);
    }
    return false;
  }, [experimentId]);

  return {
    variant,
    config,
    experimentId,
    trackConversion,
    loading,
    isInExperiment: !!experimentId
  };
};

/**
 * Helper to check if current variant matches a specific variant name
 */
export const isVariant = (currentVariant, targetVariant) => {
  return currentVariant === targetVariant;
};

/**
 * Higher-order component for A/B testing
 * 
 * Usage:
 * ```
 * const EnhancedComponent = withExperiment("job_recommendations")(({ variant, config }) => {
 *   if (variant === "treatment") {
 *     return <TreatmentUI config={config} />;
 *   }
 *   return <ControlUI />;
 * });
 * ```
 */
export const withExperiment = (featureArea) => (WrappedComponent) => {
  return function ExperimentWrapper(props) {
    const experiment = useExperiment(featureArea);
    
    if (experiment.loading) {
      return null; // Or a loading spinner
    }
    
    return <WrappedComponent {...props} {...experiment} />;
  };
};

export default useExperiment;
